import { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, ChevronRight, Check, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { useCategoryTranslation } from '../../hooks/useCategoryTranslation';

export interface CategoryNode {
  id: string;
  name: string;
  isDefault: boolean;
  subcategories: { id: string; name: string; isDefault: boolean }[];
}

export interface CategorySelection {
  expenseCategoryIds: string[];
  expenseSubcategoryIds: string[];
  incomeCategoryIds: string[];
  incomeSubcategoryIds: string[];
}

interface CategoryMultiSelectProps {
  expenseCategories: CategoryNode[];
  incomeCategories: CategoryNode[];
  selection: CategorySelection;
  onChange: (selection: CategorySelection) => void;
}

export default function CategoryMultiSelect({
  expenseCategories,
  incomeCategories,
  selection,
  onChange,
}: CategoryMultiSelectProps) {
  const { t } = useTranslation('search');
  const { translateCategoryName } = useCategoryTranslation();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const totalSelected =
    selection.expenseCategoryIds.length +
    selection.expenseSubcategoryIds.length +
    selection.incomeCategoryIds.length +
    selection.incomeSubcategoryIds.length;

  const summary = useMemo(() => {
    if (totalSelected === 0) return t('filters.allCategories');
    return t('filters.categoriesCount', { count: totalSelected });
  }, [totalSelected, t]);

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleArrayValue = (arr: string[], id: string): string[] =>
    arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id];

  const toggleExpenseCategory = (id: string) =>
    onChange({ ...selection, expenseCategoryIds: toggleArrayValue(selection.expenseCategoryIds, id) });
  const toggleExpenseSubcategory = (id: string) =>
    onChange({ ...selection, expenseSubcategoryIds: toggleArrayValue(selection.expenseSubcategoryIds, id) });
  const toggleIncomeCategory = (id: string) =>
    onChange({ ...selection, incomeCategoryIds: toggleArrayValue(selection.incomeCategoryIds, id) });
  const toggleIncomeSubcategory = (id: string) =>
    onChange({ ...selection, incomeSubcategoryIds: toggleArrayValue(selection.incomeSubcategoryIds, id) });

  const clearAll = () =>
    onChange({
      expenseCategoryIds: [],
      expenseSubcategoryIds: [],
      incomeCategoryIds: [],
      incomeSubcategoryIds: [],
    });

  const renderSection = (
    title: string,
    categories: CategoryNode[],
    selectedCategoryIds: string[],
    selectedSubcategoryIds: string[],
    onToggleCategory: (id: string) => void,
    onToggleSubcategory: (id: string) => void,
  ) => {
    if (categories.length === 0) return null;
    return (
      <div>
        <div className="px-3 py-2 bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-600 border-b border-gray-100">
          {title}
        </div>
        {categories.map((cat) => {
          const isExpanded = expanded.has(cat.id);
          const checked = selectedCategoryIds.includes(cat.id);
          const hasSubs = cat.subcategories.length > 0;
          return (
            <div key={cat.id}>
              <div className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50">
                <button
                  type="button"
                  onClick={() => onToggleCategory(cat.id)}
                  className="flex items-center gap-2 flex-1 text-left"
                >
                  <span
                    className={clsx(
                      'w-4 h-4 rounded border flex items-center justify-center shrink-0',
                      checked ? 'bg-primary-600 border-primary-600' : 'border-gray-300',
                    )}
                  >
                    {checked && <Check className="w-3 h-3 text-white" />}
                  </span>
                  <span className="text-sm text-gray-700 truncate">
                    {translateCategoryName(cat.id, cat.name, cat.isDefault)}
                  </span>
                </button>
                {hasSubs && (
                  <button
                    type="button"
                    onClick={() => toggleExpand(cat.id)}
                    className="p-0.5 hover:bg-gray-200 rounded"
                  >
                    <ChevronRight
                      className={clsx('w-4 h-4 text-gray-400 transition-transform', isExpanded && 'rotate-90')}
                    />
                  </button>
                )}
              </div>
              {hasSubs && isExpanded && (
                <div className="pl-6">
                  {cat.subcategories.map((sub) => {
                    const subChecked = selectedSubcategoryIds.includes(sub.id);
                    return (
                      <button
                        key={sub.id}
                        type="button"
                        onClick={() => onToggleSubcategory(sub.id)}
                        className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 text-left"
                      >
                        <span
                          className={clsx(
                            'w-4 h-4 rounded border flex items-center justify-center shrink-0',
                            subChecked ? 'bg-primary-600 border-primary-600' : 'border-gray-300',
                          )}
                        >
                          {subChecked && <Check className="w-3 h-3 text-white" />}
                        </span>
                        <span className="text-gray-600 truncate">
                          {translateCategoryName(sub.id, sub.name, sub.isDefault)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 hover:border-primary-400 transition-colors"
      >
        <span className="truncate">{summary}</span>
        <div className="flex items-center gap-1 shrink-0">
          {totalSelected > 0 && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); clearAll(); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); clearAll(); } }}
              className="p-0.5 hover:bg-gray-100 rounded"
            >
              <X className="w-3.5 h-3.5 text-gray-500" />
            </span>
          )}
          <ChevronDown className={clsx('w-4 h-4 text-gray-400 transition-transform', open && 'rotate-180')} />
        </div>
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto">
          {renderSection(
            t('filters.expensesSection'),
            expenseCategories,
            selection.expenseCategoryIds,
            selection.expenseSubcategoryIds,
            toggleExpenseCategory,
            toggleExpenseSubcategory,
          )}
          {renderSection(
            t('filters.incomesSection'),
            incomeCategories,
            selection.incomeCategoryIds,
            selection.incomeSubcategoryIds,
            toggleIncomeCategory,
            toggleIncomeSubcategory,
          )}
          {expenseCategories.length === 0 && incomeCategories.length === 0 && (
            <div className="px-3 py-3 text-sm text-gray-500 text-center">
              {t('filters.noCategories')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
